import { useEffect, useState } from 'react';
import { adminMe, setTwoFactorRequired } from '../../api/admin';
import CustomDomainsCard from './CustomDomainsCard';
import type { AccountRole } from '../../api/admin';
import { setNewFormAccessApi } from '../../api/access';
import { Link } from 'react-router-dom';
import {
  SsoSettingsResponse, getSsoSettings, saveSsoSettings,
} from '../../api/admin';
import type { AccountSso, IdentityProvider } from '../../shared/types';
import { PROTOCOL_LABEL } from '../../shared/types';
import { CopyUri, DomainChips, StatusPill, ago } from './SsoShared';
import { PHONE, useMediaQuery } from '../../shared/media';
import { RowCard, TableCard } from '../../components/fs';

/**
 * Settings → Account access.
 *
 * Two separate things share this page on purpose: how the TEAM signs in to Form
 * Studio, and the library of providers the account's FORMS can require of the
 * people filling them in. They are independent doors drawing on one registry —
 * saying so once here is cheaper than explaining it twice elsewhere.
 *
 * Named for access rather than SSO because it governs password sign-in just as
 * much — and in password-only mode the SSO half of the page is not shown at all.
 */
export default function AccountAccess() {
  const [data, setData] = useState<SsoSettingsResponse | null>(null);
  const [error, setError] = useState('');
  /** a card per provider instead of the 6-column table (§4G G3) */
  const phone = useMediaQuery(PHONE);
  /**
   * Things that saved fine but will surprise somebody — a domain another
   * account already claims, or none at all. Shown, not thrown: which of two
   * workspaces owns @acme.com is not ours to decide.
   */
  const [warnings, setWarnings] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  /**
   * A Member reaches this page — it is the only Settings tab they have — but
   * how the company signs in is not theirs to change, and neither is where new
   * forms land. The controls stay VISIBLE and inert with the reason above
   * them, the same treatment the credential picker gives a Member: a missing
   * control reads as a missing feature, and "why can't I change this?" is the
   * question the sentence answers.
   */
  const [canManage, setCanManage] = useState(true);

  const [hasTeamAccess, setHasTeamAccess] = useState(false);

  useEffect(() => {
    adminMe()
      .then((r) => {
        setCanManage(r.isPlatformAdmin || r.admin.role === 'owner' || r.admin.role === 'admin');
        setHasTeamAccess(!!r.features?.teamAccess);
      })
      .catch(() => undefined);
  }, []);

  const load = () => getSsoSettings().then(setData).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  /**
   * Every control saves as it changes — there is no Save button on this page.
   * A refusal (no break-glass user, a failing provider) comes back from the
   * server and is shown here, with the on-screen state reloaded so the radio
   * never sits on a setting that was not actually stored.
   */
  async function patch(next: Partial<AccountSso>) {
    if (!data) return;
    setSaving(true); setError('');
    try {
      const r = await saveSsoSettings(next);
      setData({ ...data, sso: r.sso });
      setWarnings(r.warnings || []);
    } catch (e: any) {
      setError(e.message);
      await load();
    } finally {
      setSaving(false);
    }
  }

  if (!data) {
    return <div className="fs-card pad st-col">{error ? <div className="ad-err">{error}</div> : 'Loading…'}</div>;
  }

  const { sso, providers } = data;
  const teamIds = sso.providerIds || [];
  const connected = providers.find((p) => p.id === sso.providerId) || null;
  /** every provider team sign-in is actually pointed at, in list order */
  const connectedTeam = providers.filter((p) => teamIds.includes(p.id));
  // Every protocol, SAML included — team sign-in has handled SAML since the
  // ACS landed, and filtering it out here only hid a provider that works.
  const usable = providers;
  // Nothing about a provider signing the team in applies in password-only mode,
  // so those sections are hidden rather than shown inert. What they hold stays
  // stored: turning SSO back on finds the setup exactly as it was left.
  const passwordOnly = sso.mode === 'password';

  const METHOD: Record<string, string> = { password: 'Email and password', optional: 'SSO or password', required: 'SSO only' };
  const JOIN: Record<string, string> = {
    domain: 'Matching email domain', invite: 'Invitation only', domain_roles: 'Matching email domain, role from the IdP',
  };
  const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="st-col st-wide">
      <div className="fs-page-head">
        <div className="text">
          <h1>Sign-in &amp; access</h1>
          <div className="fs-page-sub">
            How <b>your team</b> signs in, what a <b>new form</b> starts out visible to, and the
            identity providers your <b>forms</b> can require of the people filling them in.
          </div>
        </div>
      </div>

      {error && <div className="ad-err">{error}</div>}

      {warnings.map((w, i) => (
        <div key={i} className="fs-consequence">⚠ {w}</div>
      ))}

      {/* Set to single sign-on on a plan without it. Rendered only then, so
          the page is otherwise as it was. */}
      {data?.ssoOff && (
        <div className="fs-consequence warn" role="status">
          <b>Single sign-on is not part of your plan right now.</b> Your team signs in with email and
          password until it is — “Forgot password” on the sign-in page sets one for anybody who has only
          ever used single sign-on. Everything below is kept, and works again on a plan that includes it.
        </div>
      )}

      {!canManage && (
        <div className="fs-consequence">
          ⓘ You are a Member, so this page is read-only. How your team signs in and where new
          forms land are an Owner or Admin's to change.
        </div>
      )}

      {/* The design's summary (screen 28): each row says where things stand and
          jumps to the card below that changes it. Nothing is edited here, so
          no setting has two controls. */}
      <section className="fs-card st-card" aria-labelledby="st-howsignin">
        <div className="st-card-head"><h2 id="st-howsignin">How your team signs in</h2></div>
        <RowCard label="Method" actions={<button type="button" className="fs-btn text" onClick={() => jump('st-team-signin')}>Change</button>}>
          {METHOD[sso.mode] || sso.mode}
          {!passwordOnly && !!connectedTeam.length && <span className="st-muted"> · {connectedTeam.map((p) => p.name).join(', ')}</span>}
        </RowCard>
        <RowCard
          label="New people join by"
          actions={passwordOnly
            ? <Link className="fs-btn text" to="/admin/settings/users">Users</Link>
            : <button type="button" className="fs-btn text" onClick={() => jump('st-provisioning')}>Change</button>}
        >
          {passwordOnly ? 'An invitation from Users' : (JOIN[sso.provisioning] || sso.provisioning)}
          {!passwordOnly && sso.provisioning !== 'invite' && !!sso.allowedDomains.length && (
            <span className="st-muted"> · {sso.allowedDomains.join(', ')}</span>
          )}
        </RowCard>
        <RowCard label="Require 2FA for password sign-ins" actions={<button type="button" className="fs-btn text" onClick={() => jump('st-2fa')}>Change</button>}>
          {data.twoFactorRequired ? <span className="st-state ok">On</span> : <span className="st-state off">Off</span>}
        </RowCard>
        <RowCard label="New forms" actions={<button type="button" className="fs-btn text" onClick={() => jump('st-newforms')}>Change</button>}>
          {(data.newFormAccess || 'everyone') === 'everyone' ? 'Everyone in the account' : 'Only whoever makes it'}
        </RowCard>
      </section>


      {/* ---- Card 1: team sign-in ---- */}
      <section className="fs-card st-card" id="st-team-signin">
        <div className="st-card-head"><h2>Team sign-in</h2></div>
        <div className="st-card-body">
        {/* On a phone the rail that says which provider is connected, and
            whether it is healthy, sits a screen and a half below these cards
            once `.sso-cols` has stacked. This line says the same three facts
            first. Rendered only where it is shown: it repeats the rail's
            provider name and health pill, and a desktop should not carry two
            of each. */}
        {phone && !passwordOnly && !!connectedTeam.length && (
          <div className="sso-status">
            <span>Connected:</span>
            {connectedTeam.map((p) => (
              <span key={p.id} className="who">{p.name}</span>
            ))}
            <StatusPill status={connectedTeam[0].status} />
            <span className="when">
              {connectedTeam[0].status === 'live'
                ? `last sign-in ${ago(connectedTeam[0].lastSignInAt)}`
                : connectedTeam[0].checkedAt ? `checked ${ago(connectedTeam[0].checkedAt)}` : 'never checked'}
            </span>
          </div>
        )}
        <div className="sso-cols">
          <div className="main">
            <ModeCard
              on={sso.mode === 'password'} disabled={saving || !canManage}
              title="Email and password only" hint="No SSO button on the sign-in page."
              onPick={() => patch({ mode: 'password' })}
            />
            <ModeCard
              on={sso.mode === 'optional'} disabled={saving || !canManage}
              title="SSO optional" hint="Either works — passwords keep working while you roll SSO out."
              onPick={() => patch({ mode: 'optional', providerIds: teamIds.length ? teamIds : [usable[0]?.id].filter(Boolean) as number[] })}
            />
            <ModeCard
              on={sso.mode === 'required'} disabled={saving || !canManage}
              title="SSO required"
              hint={
                <>
                  Passwords stop working for everyone at <b>{sso.allowedDomains.map((d) => `@${d}`).join(', ') || 'the allowed domains'}</b>.
                  {' '}Break-glass logins can be kept per user on the Users page.
                </>
              }
              onPick={() => patch({ mode: 'required', providerIds: teamIds.length ? teamIds : [usable[0]?.id].filter(Boolean) as number[] })}
            />

            {sso.mode !== 'password' && usable.length > 0 && (
              <div className="ad-field" style={{ maxWidth: 520, marginTop: 10 }}>
                <label>Providers that can sign your team in</label>
                <div style={{ display: 'grid', gap: 6 }}>
                  {usable.map((p) => {
                    const on = teamIds.includes(p.id);
                    // The last one cannot be unticked: SSO with no provider is
                    // a save the server refuses, and a tickbox that silently
                    // fails is worse than one that will not move.
                    const lastOne = on && teamIds.length === 1;
                    return (
                      <label key={p.id} className={`fc-radio ${on ? 'on' : ''}`} style={{ cursor: lastOne ? 'default' : 'pointer' }}>
                        <input
                          type="checkbox" checked={on} disabled={saving || !canManage || lastOne}
                          onChange={() => patch({
                            providerIds: on ? teamIds.filter((id) => id !== p.id) : [...teamIds, p.id],
                          })}
                        />
                        <span>
                          <b>{p.name}</b>
                          <small>
                            {PROTOCOL_LABEL[p.protocol]}
                            {p.allowedDomains.length
                              ? ` · ${p.allowedDomains.map((d) => `@${d}`).join(', ')}`
                              : ' · no domains of its own'}
                          </small>
                        </span>
                      </label>
                    );
                  })}
                </div>
                <small style={{ color: 'var(--text-muted)' }}>
                  Only these open the account itself. A provider left unticked can still be required by a
                  form on its Access tab — that is a separate door and grants nobody a Form-Magic login.
                </small>
              </div>
            )}

            {!passwordOnly && (
              <div className="pe-checklist">
                <div className="pe-checklist-title">Before you switch this on</div>
                <div style={{ fontSize: 13, color: 'var(--ink-800)', lineHeight: 1.45 }}>
                  one signed-in break-glass admin must keep a password, or a broken IdP locks the account out.{' '}
                  <Link to="/admin/settings/users">Review the {data.userCount} user{data.userCount === 1 ? '' : 's'} →</Link>
                  {data.breakGlassCount === 0 && (
                    <div style={{ marginTop: 6, color: 'var(--danger-fg)', fontWeight: 600 }}>
                      No user is exempt from SSO yet, so "SSO required" will be refused.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="side">
            {passwordOnly ? (
              <div className="sso-password-note">
                Everyone signs in with an email and password set on the <Link to="/admin/settings/users">Users</Link> page.
                Providers in the library below are still available to individual <Link to="/admin">forms</Link>.
              </div>
            ) : (
              <ConnectedProviders providers={connectedTeam} />
            )}
          </div>
        </div>
        </div>
      </section>

      {/* ---- Card 1b: the links an admin hands out ---- */}
      {!passwordOnly && (
        <section className="fs-card st-card">
          <div className="st-card-head"><h2>Your team's sign-in links</h2></div>
          <div className="st-card-body">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Nobody has to use these — typing a work email at{' '}
            <a href="/login" target="_blank" rel="noreferrer">/login</a> finds your provider on its own,
            as long as the domain is listed. They exist for the times that is not enough: a link on an
            intranet, a tile in your IdP, or a domain another workspace also claims.
          </p>

          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-800)', margin: '0 0 4px' }}>
            Your sign-in page
          </div>
          <CopyUri uri={data.brandedLoginUrl} />
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px' }}>
            Shows your account name and one button per provider. Safe to publish anywhere your people
            will see it.
          </div>

          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-800)', margin: '0 0 4px' }}>
            Straight to the provider
          </div>
          <CopyUri uri={data.directLoginUrl} />
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Skips the page entirely and starts the handshake — the one to put behind an IdP tile.
            {teamIds.length > 1 && (
              <> With more than one provider this uses <b>{connected?.name || 'the first'}</b>; add{' '}
                <code>?provider=&lt;slug&gt;</code> to pin a different one.</>
            )}
          </div>
          </div>
        </section>
      )}

      {/* ---- Card 2: provisioning. Only means anything once SSO is on. ---- */}
      {!passwordOnly && (
        <section className="fs-card st-card" id="st-provisioning">
          <div className="st-card-head"><h2>How team members get an account</h2></div>
          <div className="st-card-body">
          <div className="sso-grid-3">
            <ModeCard
              on={sso.provisioning === 'domain'} disabled={saving}
              title="Anyone from an allowed domain" hint="First sign-in creates the user with the default role."
              onPick={() => patch({ provisioning: 'domain' })}
            />
            <ModeCard
              on={sso.provisioning === 'invite'} disabled={saving}
              title="Invite only" hint="SSO only matches users you have already added."
              onPick={() => patch({ provisioning: 'invite' })}
            />
            <ModeCard
              on={sso.provisioning === 'domain_roles'} disabled={saving}
              title="Allowed domain + role from the IdP" hint="A group claim decides admin vs superadmin."
              onPick={() => patch({ provisioning: 'domain_roles' })}
            />
          </div>

          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 14 }}>
            <div className="ad-field sso-domains-field">
              <label>Your email domains</label>
              <DomainChips value={sso.allowedDomains} onChange={(v) => patch({ allowedDomains: v })} />
              <small style={{ color: 'var(--text-muted)', display: 'block', lineHeight: 1.5 }}>
                These do <b>two</b> things. They are what the sign-in page matches on: somebody typing
                {' '}<b>you@{sso.allowedDomains[0] || 'yourcompany.com'}</b> at{' '}
                <a href="/login" target="_blank" rel="noreferrer">/login</a> is only offered your provider
                because the domain is listed here or on the provider itself. And they limit which addresses
                may be turned into users — empty means any verified email the IdP returns, which is not
                what you want with automatic creation.
              </small>
            </div>
            <div className="ad-field fs-w-320">
              <label>Default role for new SSO users</label>
              <select
                value={sso.defaultRole} disabled={saving}
                onChange={(e) => patch({ defaultRole: e.target.value as AccountRole })}
              >
                <option value="member">member — sees only what is shared with them</option>
                <option value="admin">admin — runs the workspace, every form</option>
                <option value="owner">owner — everything, including billing</option>
              </select>
              <small style={{ color: 'var(--text-muted)' }}>
                Replaces <code>ADMIN_SSO_DEFAULT_ROLE</code>. Starts on member: an admin sees every form,
                whatever it is shared with.
              </small>
            </div>
          </div>

          {sso.provisioning === 'domain_roles' && (
            <div className="ad-field" style={{ maxWidth: 420 }}>
              <label>Claim that carries the role</label>
              <input
                value={sso.roleClaim || ''}
                placeholder="groups"
                onChange={(e) => patch({ roleClaim: e.target.value })}
              />
              <small style={{ color: 'var(--text-muted)' }}>
                Any value in this claim that matches a superadmin group grants that role; everyone else gets the default.
              </small>
            </div>
          )}
          </div>
        </section>
      )}

      {/* ---- Card 3: the provider library. Shown in every mode — a form can
           require a provider while the team signs in with passwords. ---- */}
      <TwoFactorPolicyCard
        value={!!data.twoFactorRequired}
        canManage={canManage}
        hasTeamAccess={hasTeamAccess}
        onSaved={(v) => setData({ ...data, twoFactorRequired: v })}
      />

      <NewFormAccessCard
        value={data.newFormAccess || 'everyone'}
        canManage={canManage}
        onSaved={(v) => setData({ ...data, newFormAccess: v })}
      />

      <TableCard
        title="Identity providers"
        hint="One library, two uses. A provider can sign your team in, be required by a form, or both — a form picks one on its Access tab."
        action={(
          <Link className="fs-btn primary sm" to="/admin/settings/access/providers/new">
            + Add identity provider
          </Link>
        )}
        footer={(
          <div className="st-callback">
            Give your IdP administrator this redirect URI. One is enough for every provider and every form on this account —
            which form someone came from travels in the OIDC <code>state</code> parameter, so they land back on that exact form:
            <CopyUri uri={data.callbackUri} />
            <div>
              If an IdP administrator insists on a URI dedicated to one app, each provider also answers on its own path —{' '}
              <code>/api/sso/{data.accountSlug}/p/&lt;provider&gt;/callback</code>, shown on the provider itself.
            </div>
          </div>
        )}
      >
        {providers.length === 0 ? (
          <div className="st-card-body st-muted">
            No providers yet. Add one to let your team — or the people filling in a form — sign in with their own company account.
          </div>
        ) : phone ? (
          <div className="pv-cards">
            {providers.map((p) => <ProviderCard key={p.id} p={p} />)}
          </div>
        ) : (
          <table className="fs-table">
            <thead>
              <tr><th>Provider</th><th>Protocol</th><th>Domains</th><th>Used by</th><th>Health</th><th /></tr>
            </thead>
            <tbody>
              {providers.map((p) => <ProviderRow key={p.id} p={p} />)}
            </tbody>
          </table>
        )}
      </TableCard>

      <CustomDomainsCard canManage={canManage} />
    </div>
  );
}

function ModeCard({ on, title, hint, onPick, disabled }: {
  on: boolean; title: string; hint: React.ReactNode; onPick: () => void; disabled?: boolean;
}) {
  return (
    <label className={`fc-radio ${on ? 'on' : ''}`}>
      <input type="radio" checked={on} disabled={disabled} onChange={onPick} />
      <span><b>{title}</b><small>{hint}</small></span>
    </label>
  );
}

/** The right-hand rail: every provider team sign-in is actually pointed at. */
function ConnectedProviders({ providers }: { providers: IdentityProvider[] }) {
  if (!providers.length) {
    return (
      <div className="pe-preview">
        <div className="pe-pv-head">Connected provider</div>
        <div style={{ padding: 14, fontSize: 13, color: 'var(--danger-fg)', lineHeight: 1.5 }}>
          No provider is selected, so nobody can sign in with SSO. Add one below, then tick it above.
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {providers.map((p) => <ConnectedProvider key={p.id} provider={p} />)}
    </div>
  );
}

function ConnectedProvider({ provider }: { provider: IdentityProvider }) {
  return (
    <div className="pe-preview">
      <div className="pe-pv-head">Signs your team in</div>
      <div style={{ padding: 14 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{provider.name}</span>
          <StatusPill status={provider.status} />
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{PROTOCOL_LABEL[provider.protocol]}</div>
        {provider.config.discoveryUrl && (
          <div style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--ink-700)', wordBreak: 'break-all', margin: '4px 0' }}>
            {provider.config.discoveryUrl}
          </div>
        )}
        <div style={{ fontSize: 12.5, color: 'var(--ink-700)', marginTop: 4 }}>
          Domains: {provider.allowedDomains.length
            ? provider.allowedDomains.map((d) => <b key={d} style={{ marginRight: 6 }}>{d}</b>)
            : <span style={{ color: 'var(--text-faint)' }}>any the IdP returns</span>}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
          Last successful sign-in: {provider.lastSignInAt ? ago(provider.lastSignInAt) : 'none yet'}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
          <Link className="fc-act" to={`/admin/settings/access/providers/${provider.id}`} style={{ textDecoration: 'none' }}>Edit</Link>
          <Link className="fc-act" to={`/admin/settings/access/providers/${provider.id}?check=1`} style={{ textDecoration: 'none' }}>Test connection</Link>
        </div>
      </div>
    </div>
  );
}

/**
 * One provider as a card (§4G G3): the six columns of `ProviderRow` read as
 * four lines and a button pair. Edit and Test are the same two links the row
 * carries, to the same two URLs.
 */
function ProviderCard({ p }: { p: IdentityProvider }) {
  const usage = p.usage;
  return (
    <div className="pv-card">
      <div className="name">
        {p.name}
        <StatusPill status={p.status} />
      </div>
      <div className="proto">
        {PROTOCOL_LABEL[p.protocol]}
        {' · '}
        {p.status === 'live' ? `last ${ago(p.lastSignInAt)}`
          : p.checkedAt ? `checked ${ago(p.checkedAt)}${p.status === 'checks_passed' ? ' · no real sign-in yet' : ''}`
            : 'never checked'}
      </div>
      <div className="domains">
        {p.allowedDomains.length
          ? p.allowedDomains.map((d) => `@${d}`).join(', ')
          : 'any domain the IdP returns'}
      </div>
      <div className="usage">
        {usage?.teamSignIn && <span className="ad-pill sso">Team sign-in</span>}
        {!!usage?.forms.length && (
          <span className="ad-pill simple">{usage.forms.length} form{usage.forms.length === 1 ? '' : 's'}</span>
        )}
        {!usage?.teamSignIn && !usage?.forms.length && <span className="sso-sub">not used yet</span>}
      </div>
      <div className="pair">
        <Link className="fc-act" to={`/admin/settings/access/providers/${p.id}`} style={{ textDecoration: 'none' }}>Edit</Link>
        <Link className="fc-act" to={`/admin/settings/access/providers/${p.id}?check=1`} style={{ textDecoration: 'none' }}>Test</Link>
      </div>
    </div>
  );
}

function ProviderRow({ p }: { p: IdentityProvider }) {
  const usage = p.usage;
  return (
    <tr>
      <td style={{ fontWeight: 600 }}>{p.name}</td>
      <td className="st-mono">{PROTOCOL_LABEL[p.protocol]}</td>
      <td className="st-muted">{p.allowedDomains.join(', ') || <span className="st-faint">any</span>}</td>
      <td>
        {usage?.teamSignIn && <span className="ad-pill sso">Team sign-in</span>}
        {!!usage?.forms.length && (
          <>
            {usage.teamSignIn && ' '}
            <span className="ad-pill simple">{usage.forms.length} form{usage.forms.length === 1 ? '' : 's'}</span>
            <div className="sso-sub">
              {usage.forms.map((f) => (
                <Link key={f.id} to={`/admin/forms/${f.id}/access`} style={{ marginRight: 8 }}>{f.name}</Link>
              ))}
            </div>
          </>
        )}
        {!usage?.teamSignIn && !usage?.forms.length && <span className="st-faint">not used yet</span>}
      </td>
      <td>
        <StatusPill status={p.status} />
        <div className="sso-sub">
          {p.status === 'live' ? `last ${ago(p.lastSignInAt)}`
            : p.checkedAt ? `checked ${ago(p.checkedAt)}${p.status === 'checks_passed' ? ' · no real sign-in yet' : ''}`
              : 'never checked'}
        </div>
      </td>
      <td>
        <span className="fc-actions">
          <Link className="fs-btn text" to={`/admin/settings/access/providers/${p.id}`}>Edit</Link>
          <Link className="fs-btn text" to={`/admin/settings/access/providers/${p.id}?check=1`}>Test</Link>
        </span>
      </td>
    </tr>
  );
}

/**
 * Where a new form lands (Q3).
 *
 * "Visible to everyone" for existing AND new accounts. A brand-new account is
 * usually one person, so private-by-default is invisible on day one and
 * surprising on the day the second person arrives — before anybody has learned
 * that sharing exists. It is a setting rather than a rule because the accounts
 * for which the other answer is right know who they are.
 *
 * Saves as it changes, like every other control on this page.
 */
/**
 * "Everyone here signs in with two-factor" (T2, decision 4). Per-user 2FA is
 * free for anybody from Settings → Security; REQUIRING it of the whole team
 * is an org control, so switching it on rides the teamAccess feature. The
 * control stays visible and inert without the feature, with the reason
 * beside it — the same treatment the rest of this page gives a Member.
 */
function TwoFactorPolicyCard({ value, canManage, hasTeamAccess, onSaved }: {
  value: boolean;
  canManage: boolean;
  hasTeamAccess: boolean;
  onSaved: (v: boolean) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Switching OFF is always allowed — losing the feature must never trap the setting.
  const locked = !canManage || (!hasTeamAccess && !value);

  async function toggle(next: boolean) {
    if (next === value || saving) return;
    setSaving(true); setError('');
    try { onSaved((await setTwoFactorRequired(next)).twoFactorRequired); }
    catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fs-card" id="st-2fa">
      <div className="fs-card-head">
        <h2>Two-factor authentication</h2>
        <div className="hint">
          Anyone can turn on two-factor for themselves from Settings → Profile &amp; security. This makes it
          the rule: every password sign-in must have it, and one without it is walked through
          setup before anything else. Sign-ins through your identity provider are exempt — the
          provider owns that policy.
        </div>
      </div>
      <div style={{ padding: '14px 20px 18px' }}>
        {error && <div className="ad-err" style={{ marginBottom: 10 }}>{error}</div>}
        <label className={`fs-choice${value ? ' on' : ''}`}>
          <input type="checkbox" checked={value} disabled={saving || locked}
            onChange={(e) => toggle(e.target.checked)} />
          <span>
            <b>Require two-factor for everyone</b>
            <small>
              {!hasTeamAccess && !value
                ? 'Requiring it account-wide comes with the plans that include team access.'
                : 'Applies from each person’s next password sign-in.'}
            </small>
          </span>
        </label>
      </div>
    </div>
  );
}

function NewFormAccessCard({ value, canManage, onSaved }: {
  value: 'everyone' | 'creator';
  canManage: boolean;
  onSaved: (v: 'everyone' | 'creator') => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function pick(next: 'everyone' | 'creator') {
    if (next === value || saving) return;
    setSaving(true); setError('');
    try { onSaved((await setNewFormAccessApi(next)).newFormAccess); }
    catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fs-card" id="st-newforms">
      <div className="fs-card-head">
        <h2>New forms</h2>
        <div className="hint">
          What a form is visible to the moment it is made. Either way, a form made
          inside a folder takes on that folder&rsquo;s access instead.
        </div>
      </div>
      <div style={{ padding: '14px 20px 18px' }}>
        {error && <div className="ad-err" style={{ marginBottom: 10 }}>{error}</div>}
        <label className={`fs-choice${value === 'everyone' ? ' on' : ''}`}>
          <input type="radio" checked={value === 'everyone'} disabled={saving || !canManage}
            onChange={() => pick('everyone')} />
          <span>
            <b>Visible to everyone in the account</b>
            <small>Anyone who can sign in can reach it and edit it, until somebody restricts it.</small>
          </span>
        </label>
        <label className={`fs-choice${value === 'creator' ? ' on' : ''}`}>
          <input type="radio" checked={value === 'creator'} disabled={saving || !canManage}
            onChange={() => pick('creator')} />
          <span>
            <b>Private to the person who makes it</b>
            <small>
              Only its maker — plus Owners and Admins, who see every form — until they share it.
            </small>
          </span>
        </label>
      </div>
    </div>
  );
}
